import React, { useEffect, useMemo, useState } from 'react';
import InfoDialog from '../../../../components/shared/InfoDialog';
import { useTranslation } from 'react-i18next';
import { Box, InputAdornment, Link, Stack, Typography } from '@mui/material';
import CusLabelSelect from '../../../../components/material-ui/CusLabelSelect';
import { ENUM_PAYOUT_TYPE } from '../../../../enums/enums';
import CusInput from '../../../../components/material-ui/CusInput';
import { API_FINANCE } from '../../../../api/finance';
import useToast from '../../../../hooks/useToast';
import ShowContainer from '../../../../components/material-ui/ShowContainer';
import PaymentError from './PaymentError';
import { useAppSelector } from '../../../../store';

const MANUAL = 'manual';
const OTHER_VALUE = 'otherValue';

export default function PaymentMakeModal({
  openPayoutId,
  setOpenPayoutId,
  info,
  updateInfo,
  casinoId,
  updateGates
}) {
  const { t } = useTranslation();
  const { successToast, errorToast } = useToast();
  const lang = useAppSelector((state) => state.site.lang);
  const curLocale = lang == 'ru' ? 'ru' : 'en';

  const id = info[0];
  const amount = info[4];
  const currency = info[5];
  const type = info[9];

  const [openErrorId, setOpenErrorId] = useState(0);
  const [reply, setReply] = useState(null);
  const [gates, setGates] = useState([]);

  const [fixedPercentOptions, setFixedPercentOptions] = useState([]);
  const methodsOptions = useMemo(() => {
    if (!gates.length) return [];

    const methods = [{ value: MANUAL, label: 'Manually' }];

    gates.forEach((gate) => {
      const payout = gate.payout;
      if (!payout) return;

      const isCurrency = payout.currencyList?.includes(currency);
      const isType = payout.typeList?.includes(type);

      if (!isCurrency) return;
      if (!isType) return;

      const methodsPayment = payout.methodList?.[currency]?.[type];
      if (methodsPayment) {
        const tempMethods = methodsPayment.map((el) => ({
          value: `${gate.code}.${el}`,
          label: `${gate.label} ${el}`
        }));
        methods.push(...tempMethods);
        return;
      }

      const method = { value: gate.code, label: gate.label };
      methods.push(method);
    });

    return methods;
  }, [gates.length, currency, type]);

  const [method, setMethod] = useState(null);
  const [comment, setComment] = useState('');
  const [fixedPercent, setFixedPercent] = useState();
  const [percent, setPercent] = useState();

  useEffect(() => {
    setMethod(null);
    setComment(null);

    if (!amount || !currency) return;
    if (!casinoId) return;

    updateGates(casinoId)
      .then((gates) => setGates(gates))
      .catch(console.error);

    API_FINANCE.payout.commission({ amount, currency, casinoId: casinoId }).then((e) => {
      const list = e.data.list;
      const fixedPercents = list.map((el) => {
        let label = `${el.gateName} (`;
        if (el.percent) label += `${el.percent}%`;
        if (el.fixed && el.percent) label += '+';
        if (el.fixed) label += `${el.fixed} ${currency}`;
        label += ')';

        return {
          label: label,
          value: el.gate,
          fp: `${el.fixed}+${el.percent}`,
          minimum: el.minimum
        };
      });
      fixedPercents.push({ value: OTHER_VALUE, label: 'Other value' });
      setFixedPercentOptions(fixedPercents);
    });
  }, [id, casinoId]);

  const payoutFields = [{ name: 'Payment amount', value: `${amount} ${currency}` }];

  if (type == ENUM_PAYOUT_TYPE.phone) {
    payoutFields.push({ name: 'To the phone', value: info[17] });
    payoutFields.push({ name: 'Bank name', value: info[18] });
  }
  if (type == ENUM_PAYOUT_TYPE.card) {
    payoutFields.push({ name: 'To the card', value: info[10], checkBin: true });
    payoutFields.push({ name: 'Payee', value: info[11] });
  }
  if (type == ENUM_PAYOUT_TYPE.acc) {
    payoutFields.push({ name: 'To the account', value: info[10], checkBin: true });
    payoutFields.push({ name: 'Payee', value: info[11] });
  }
  if (type == ENUM_PAYOUT_TYPE.crypto) {
    payoutFields.push({ type: 'column', name: 'Crypto wallet', value: info[16] });
  }

  const onSave = () => {
    let gateFee = null;
    if (method == MANUAL) {
      if (fixedPercent == OTHER_VALUE) {
        gateFee = `0+${+percent}`;
      } else {
        const curFP = fixedPercentOptions.find((el) => el.value == fixedPercent).fp;
        console.log('curFP', curFP);
        gateFee = curFP;
      }
    }

    if (!method) {
      errorToast('Be sure to fill out the payment method');
      return;
    }

    API_FINANCE.payout
      .payout({ invoiceId: id, method, comment, gateFee })
      .then((e) => {
        const data = e.data.data;
        const status = data?.status;

        if (data?.pageUrl) {
          const newWindow = window.open(data?.pageUrl, '_blank');
          if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
            window.location.href = data?.pageUrl;
          }
        } else {
          if (status) {
            if (status == 6) successToast('Payment completed successfully');
            if ([9, 10].includes(status)) errorToast('An error occurred');
          } else {
            successToast('Payment completed successfully');
          }
        }

        updateInfo(id, data);
        setOpenPayoutId(false);
      })
      .catch((e) => {
        const error = e.error;
        setOpenErrorId(id);
        setReply(error);
      });
  };

  const titleId = openPayoutId ? `#${openPayoutId}` : '';

  const minimumCommission = fixedPercentOptions.find((el) => el.value == fixedPercent)?.minimum;

  return (
    <>
      <InfoDialog
        open={openPayoutId}
        maxWidth="sm"
        title={`${t('Application Invoice')} ${titleId}`}
        saveText={'Pay'}
        onClose={() => setOpenPayoutId(false)}
        onSaveClick={onSave}>
        <Stack gap={1}>
          <CusLabelSelect
            label={'Payout method'}
            options={methodsOptions}
            value={method}
            onChange={setMethod}
          />
          {payoutFields.map((field) => (
            <Stack direction={field.type || 'row'} key={field.name} gap={1} alignItems={'center'}>
              <Typography fontSize={14} color="textSecondary" minWidth={160}>
                {t(field.name)}:
              </Typography>
              <Typography fontWeight={600}>{field.value}</Typography>
              <ShowContainer condition={field.checkBin}>
                <Link
                  component={'a'}
                  href={`https://bincheck.io/${curLocale}/details/${field.value.slice(0, 6)}`}
                  color="primary"
                  target="_blank">
                  {t('Check BIN')}
                </Link>
              </ShowContainer>
            </Stack>
          ))}

          <ShowContainer condition={method == MANUAL}>
            <Stack direction={'row'} gap={1}>
              <Typography fontSize={14} color="textSecondary" minWidth={160}>
                {t('Commission percentage')}:
              </Typography>

              <Stack flex={1} gap={1}>
                <CusLabelSelect
                  value={fixedPercent}
                  onChange={setFixedPercent}
                  options={fixedPercentOptions}
                />

                <ShowContainer condition={fixedPercent == OTHER_VALUE}>
                  <CusInput
                    value={percent}
                    type="number"
                    onChange={(e) => setPercent(e.target.value)}
                    slotProps={{
                      input: {
                        endAdornment: <InputAdornment position="end">%</InputAdornment>
                      }
                    }}
                  />
                </ShowContainer>
              </Stack>
            </Stack>

            <ShowContainer condition={minimumCommission}>
              <Stack direction={'row'} gap={1}>
                <Typography fontSize={14} color="textSecondary" minWidth={160}>
                  {t('Minimum amount')}:
                </Typography>
                <Stack flex={1} gap={1}>
                  <Typography color="textPrimary">
                    {minimumCommission} {currency}
                  </Typography>
                </Stack>
              </Stack>
            </ShowContainer>
          </ShowContainer>

          <CusInput
            label={'Comment'}
            rows={3}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
          />
        </Stack>
      </InfoDialog>

      <PaymentError
        openErrorId={openErrorId}
        reply={reply}
        setOpenErrorId={setOpenErrorId}
        setReply={setReply}
      />
    </>
  );
}
